import React, { Fragment, useContext, useState } from "react";
import propTypes from "prop-types";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, GridLegacy as Grid } from "@mui/material";

import { api, AssetLocation, IAssetLocationCreate, PrinterLabelType } from "../../../logic/api";

import { SessionContext } from "../../../utility/useSession";

import EditableField, { EditableFieldTypes } from "../../../components/editable/EditableField/EditableField";
import EditableGroup, { EditableGroupType } from "../../../components/editable/EditableGroup/EditableGroup";
import NotchedCard from "../../../components/cards/NotchedCard/NotchedCard";
import { ProgressDialog } from "../../../components/notifications/progressDialog/progressDialog";
import ErrorDialog from "../../../components/notifications/errorDialog/ErrorDialog";
import { IPrintJob, useLabelPrinter } from "../../../components/labels";
import { useLabelTemplates } from "../../../components/labels/labelTemplates";
import { useTranslation } from "react-i18next";

const LocationCreateSequentialDialog: React.FunctionComponent<{
    create: IAssetLocationCreate;
    show: boolean;
    onClose: (created: boolean) => void;
}> = (props) => {
    const session = useContext(SessionContext);
    const { t } = useTranslation();
    const createLocation = api.hooks.assetLocation.create();
    const [startAt, setStartAt] = useState(1);
    const [endAt, setEndAt] = useState(10);
    const [print, setPrint] = useState(false);

    const { labelPrinter } = useLabelPrinter();
    const { generateLocationPrintJob } = useLabelTemplates();

    const save = async () => {
        if (location == null) {
            return;
        }

        const printJob: IPrintJob = {
            type: PrinterLabelType.boxLane,
            arrayOfValues: []
        };

        for (let i = startAt; i < (endAt + 1); i++) {
            const create = { ...props.create, name: `${props.create.name}${i}` };
            await new Promise<boolean>((accept) => {
                createLocation.send(session.current.context, create, (result) => {
                    if (result) {
                        const job = generateLocationPrintJob(result);
                        if (job.arrayOfValues != null) {
                            printJob.type = job.type;
                            if (printJob.arrayOfValues == null) {
                                printJob.arrayOfValues = [];
                            }
                            printJob.arrayOfValues.push(job.arrayOfValues[0]);
                        }

                    }

                    accept(true);
                });
            });
        }

        if (printJob.arrayOfValues != null) {
            if (printJob.arrayOfValues.length > 0) {

                if (print == true) {
                    labelPrinter.print(printJob);
                }

            }
        }

        props.onClose(true);
    }

    return <Dialog open={props.show}>
        <DialogTitle>
            <Fragment>{t("locations.createSeqLocations")}</Fragment>
        </DialogTitle>
        <DialogContent>
            <ProgressDialog status={createLocation.status}></ProgressDialog>
            <ErrorDialog err={createLocation.err}></ErrorDialog>
            {location != null && (
                <Fragment>
                    <div style={{ marginTop: 20 }}>
                        <NotchedCard label={"Range"}>
                            <EditableGroup<unknown>
                                id="locSettings"
                                testId="location-create-sequential-group"
                                editType={EditableGroupType.alwaysEditing}
                                data={{}}
                                onDataChanged={async () => {
                                    return true;
                                }}
                            >
                                <Grid container>

                                    <EditableField<AssetLocation, string>
                                        inputType={EditableFieldTypes.text}
                                        label={t("input.labels.name")}
                                        readOnly={true}
                                        alwaysVisible={true}
                                        onProvide={() => {
                                            return `${props.create.name}${startAt}-${endAt}`;
                                        }}
                                    />
                                    <EditableField<unknown, string>
                                        inputType={EditableFieldTypes.empty}
                                        label={""}
                                    />
                                    <EditableField<AssetLocation, number>
                                        inputType={EditableFieldTypes.number}
                                        label={t("input.labels.startAt")}
                                        onProvide={() => startAt}
                                        onChange={(data, value) => {
                                            setStartAt(value);
                                        }}
                                    />
                                    <EditableField<AssetLocation, number>
                                        inputType={EditableFieldTypes.number}
                                        label={t("input.labels.endAt")}
                                        onProvide={() => endAt}
                                        onChange={(data, value) => {
                                            setEndAt(value);
                                        }}
                                    />
                                    <EditableField<unknown, string>
                                        inputType={EditableFieldTypes.empty}
                                        label={""}
                                    />
                                    <EditableField<AssetLocation, boolean>
                                        inputType={EditableFieldTypes.switch}
                                        label={t("input.labels.print")}
                                        onProvide={() => print}
                                        onChange={(data, value) => {
                                            setPrint(value);
                                        }}
                                    />
                                </Grid>
                            </EditableGroup>
                        </NotchedCard>
                    </div>
                </Fragment>
            )}

        </DialogContent>
        <DialogActions>
            <Button onClick={() => {
                props.onClose(false);
            }}>
                {t("general.cancel")}
            </Button>

            <Button disabled={startAt > endAt} onClick={() => {
                save();
            }}>
                <b>{t("general.create")}</b>
            </Button>
        </DialogActions>
    </Dialog>
}

LocationCreateSequentialDialog.propTypes = {
    create: propTypes.any,
    show: propTypes.bool.isRequired,
    onClose: propTypes.func.isRequired
}
export default LocationCreateSequentialDialog